'use client';

import type { ComponentProps } from 'react';
import {
  Show as ClerkShow,
  SignIn as ClerkSignIn,
  SignUp as ClerkSignUp,
  UserButton as ClerkUserButton,
  useAuth as useClerkAuth,
  useClerk as useClerkHook,
  useSignIn as useClerkSignIn,
  useSignUp as useClerkSignUp,
  useUser as useClerkUser,
} from '@clerk/nextjs';

let hasWarnedMissingProvider = false;

function warnMissingProvider() {
  if (hasWarnedMissingProvider) return;
  hasWarnedMissingProvider = true;

  if (process.env.NODE_ENV !== 'production') {
    console.warn(
      '[auth] ClerkProvider fehlt in dieser Render-Umgebung. Fallback auf signed-out Zustand aktiv.',
    );
  }
}

const authFallback = {
  isLoaded: true,
  isSignedIn: false,
  userId: null,
  sessionId: null,
  actor: null,
  orgId: null,
  orgRole: null,
  orgSlug: null,
  orgPermissions: null,
  has: () => false,
  getToken: async () => null,
  signOut: async () => undefined,
} as unknown as ReturnType<typeof useClerkAuth>;

const userFallback = {
  isLoaded: true,
  isSignedIn: false,
  user: null,
} as unknown as ReturnType<typeof useClerkUser>;

const signInFallback = {
  isLoaded: true,
  signIn: null,
  setActive: async () => undefined,
} as unknown as ReturnType<typeof useClerkSignIn>;

const signUpFallback = {
  isLoaded: true,
  signUp: null,
  setActive: async () => undefined,
} as unknown as ReturnType<typeof useClerkSignUp>;

const clerkFallback = {
  signOut: async () => undefined,
  openSignIn: () => undefined,
  closeSignIn: () => undefined,
  openSignUp: () => undefined,
  closeSignUp: () => undefined,
} as unknown as ReturnType<typeof useClerkHook>;

export function useAuth(): ReturnType<typeof useClerkAuth> {
  try {
    return useClerkAuth();
  } catch {
    warnMissingProvider();
    return authFallback;
  }
}

export function useUser(): ReturnType<typeof useClerkUser> {
  try {
    return useClerkUser();
  } catch {
    warnMissingProvider();
    return userFallback;
  }
}

export function useClerk(): ReturnType<typeof useClerkHook> {
  try {
    return useClerkHook();
  } catch {
    warnMissingProvider();
    return clerkFallback;
  }
}

export function useSignIn(): ReturnType<typeof useClerkSignIn> {
  try {
    return useClerkSignIn();
  } catch {
    warnMissingProvider();
    return signInFallback;
  }
}

export function useSignUp(): ReturnType<typeof useClerkSignUp> {
  try {
    return useClerkSignUp();
  } catch {
    warnMissingProvider();
    return signUpFallback;
  }
}

type ShowProps = ComponentProps<typeof ClerkShow>;

export function Show({ when, fallback = null, children }: ShowProps) {
  const { isLoaded, isSignedIn } = useAuth();

  if (!isLoaded) return <>{fallback}</>;

  const shouldShow =
    (when === 'signed-in' && isSignedIn) || (when === 'signed-out' && !isSignedIn);

  return shouldShow ? <>{children}</> : <>{fallback}</>;
}

export function SignIn(props: ComponentProps<typeof ClerkSignIn>) {
  try {
    return <ClerkSignIn {...props} />;
  } catch {
    warnMissingProvider();
    return null;
  }
}

export function SignUp(props: ComponentProps<typeof ClerkSignUp>) {
  try {
    return <ClerkSignUp {...props} />;
  } catch {
    warnMissingProvider();
    return null;
  }
}

export function UserButton(props: ComponentProps<typeof ClerkUserButton>) {
  try {
    return <ClerkUserButton {...props} />;
  } catch {
    warnMissingProvider();
    return null;
  }
}
